import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFFFF0] to-[#F5F5DC]">
      {/* Back Button */}
      <div className="sticky top-0 bg-[#FFFFF0]/80 backdrop-blur-sm z-10 px-4 py-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#4A154B] hover:text-[#C9A689] transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back</span>
        </button>
      </div>

      {/* Content Container */}
      <div className="max-w-4xl mx-auto px-4 pb-20">
        <div className="bg-white/95 backdrop-blur-md rounded-2xl p-8 md:p-16 shadow-xl">
          <h1 className="font-serif text-4xl md:text-5xl text-[#4A154B] text-center mb-2">PRIVACY POLICY</h1>
          <p className="text-center text-[#C9A689] font-medium mb-10">Last Updated: January 16, 2026</p>
          
          <hr className="border-[#C9A689]/30 mb-10" />

          {/* Section 1 */}
          <section className="mb-12">
            <h2 className="font-serif text-2xl text-[#4A154B] mb-4 pb-2 border-b-2 border-[#C9A689]">1. INTRODUCTION</h2>
            <p className="text-[#4A154B] leading-relaxed mb-4">
              Welcome to SalonO Rewards ("we," "our," or "us"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our loyalty rewards platform (the "Service").
            </p>
            <p className="text-[#4A154B] leading-relaxed">
              By using our Service, you agree to the collection and use of information in accordance with this Privacy Policy. If you do not agree with our policies and practices, please do not use our Service.
            </p>
          </section>

          {/* Section 2 */}
          <section className="mb-12">
            <h2 className="font-serif text-2xl text-[#4A154B] mb-4 pb-2 border-b-2 border-[#C9A689]">2. INFORMATION WE COLLECT</h2>
            
            <h3 className="font-serif text-xl text-[#C9A689] mt-6 mb-3">2.1 Personal Information You Provide</h3>
            <p className="text-[#4A154B] leading-relaxed mb-4">
              We collect the following personal information that you voluntarily provide when you register for and use our Service:
            </p>
            
            <div className="bg-gradient-to-r from-[#C9A689]/10 to-[#C9A689]/5 border-l-4 border-[#C9A689] rounded-r-lg p-5 mb-4">
              <p className="font-semibold text-[#4A154B] mb-2">Required Information:</p>
              <ul className="list-disc ml-6 text-[#4A154B] space-y-1">
                <li><strong>Name:</strong> First and last name</li>
                <li><strong>Email Address:</strong> Used for account access and communications</li>
                <li><strong>Phone Number:</strong> Used for account security and notifications</li>
                <li><strong>Date of Birth:</strong> Used for birthday rewards and age verification</li>
              </ul>
              <p className="font-semibold text-[#4A154B] mt-4 mb-2">Optional Information:</p>
              <ul className="list-disc ml-6 text-[#4A154B] space-y-1">
                <li>Gender/pronouns (for personalized service)</li>
                <li>Service preferences</li>
                <li>Profile photo</li>
              </ul>
            </div>

            <h3 className="font-serif text-xl text-[#C9A689] mt-6 mb-3">2.2 Automatically Collected Information</h3>
            <p className="text-[#4A154B] leading-relaxed mb-2">When you use our Service, we automatically collect certain information, including:</p>
            <ul className="list-disc ml-6 text-[#4A154B] space-y-1 mb-4">
              <li><strong>Usage Data:</strong> Points earned and redeemed, services purchased, rewards claimed, transaction history, login dates and times</li>
              <li><strong>Technical Data:</strong> IP address, QR code scan data, session information, cookies and similar tracking technologies</li>
            </ul>

            <h3 className="font-serif text-xl text-[#C9A689] mt-6 mb-3">2.3 Information from Third Parties</h3>
            <p className="text-[#4A154B] leading-relaxed">
              We do not purchase or receive personal information from third-party data brokers. Any information we receive comes directly from you or from your interactions with our Service.
            </p>
          </section>

          {/* Section 3 */}
          <section className="mb-12">
            <h2 className="font-serif text-2xl text-[#4A154B] mb-4 pb-2 border-b-2 border-[#C9A689]">3. HOW WE USE YOUR INFORMATION</h2>
            
            <h3 className="font-serif text-xl text-[#C9A689] mt-6 mb-3">3.1 Service Delivery</h3>
            <ul className="list-disc ml-6 text-[#4A154B] space-y-1 mb-4">
              <li>Create and manage your loyalty account</li>
              <li>Process points earned from purchases</li>
              <li>Enable reward redemptions</li>
              <li>Generate and validate QR codes for in-salon use</li>
              <li>Track your tier status and benefits</li>
            </ul>

            <h3 className="font-serif text-xl text-[#C9A689] mt-6 mb-3">3.2 Communications</h3>
            <ul className="list-disc ml-6 text-[#4A154B] space-y-1 mb-4">
              <li>Send magic link authentication emails</li>
              <li>Notify you of points earned</li>
              <li>Send reward redemption confirmations</li>
              <li>Deliver birthday bonus notifications</li>
              <li>Send promotional offers (with your consent)</li>
              <li>Respond to your inquiries and support requests</li>
            </ul>

            <h3 className="font-serif text-xl text-[#C9A689] mt-6 mb-3">3.3 Business Operations</h3>
            <ul className="list-disc ml-6 text-[#4A154B] space-y-1 mb-4">
              <li>Analyze usage patterns and customer behavior</li>
              <li>Improve our Service and develop new features</li>
              <li>Generate analytics and reports for salon management</li>
              <li>Prevent fraud and ensure security</li>
              <li>Comply with legal obligations</li>
            </ul>

            <h3 className="font-serif text-xl text-[#C9A689] mt-6 mb-3">3.4 Marketing (Optional)</h3>
            <p className="text-[#4A154B] leading-relaxed mb-2">
              Send promotional emails about new services and rewards, provide personalized offers based on your preferences, and share updates about the loyalty program.
            </p>
            <div className="bg-gradient-to-r from-[#C9A689]/10 to-[#C9A689]/5 border-l-4 border-[#C9A689] rounded-r-lg p-5">
              <p className="text-[#4A154B]">
                <strong>You can opt out of marketing communications at any time</strong> by clicking the "unsubscribe" link in our emails or contacting us directly.
              </p>
            </div>
          </section>

          {/* Section 4 */}
          <section className="mb-12">
            <h2 className="font-serif text-2xl text-[#4A154B] mb-4 pb-2 border-b-2 border-[#C9A689]">4. HOW WE SHARE YOUR INFORMATION</h2>
            <p className="text-[#4A154B] leading-relaxed mb-4">
              <strong>We do not sell, rent, or trade your personal information to third parties.</strong> We may share your information only in the following circumstances:
            </p>

            <h3 className="font-serif text-xl text-[#C9A689] mt-6 mb-3">4.1 With Salon Staff and Management</h3>
            <p className="text-[#4A154B] leading-relaxed mb-4">
              Your information is shared with authorized staff at the salon where you hold membership to greet you by name, process your transactions, redeem your rewards, and provide personalized service.
            </p>

            <h3 className="font-serif text-xl text-[#C9A689] mt-6 mb-3">4.2 With Service Providers</h3>
            <p className="text-[#4A154B] leading-relaxed mb-2">We may share your information with trusted third-party service providers:</p>
            <ul className="list-disc ml-6 text-[#4A154B] space-y-1 mb-4">
              <li><strong>Email Service Provider (Resend):</strong> To send transactional and promotional emails</li>
              <li><strong>Cloud Hosting Provider:</strong> To store data securely</li>
              <li><strong>Analytics Services:</strong> To understand usage patterns (anonymized where possible)</li>
            </ul>

            <h3 className="font-serif text-xl text-[#C9A689] mt-6 mb-3">4.3 For Legal Compliance</h3>
            <p className="text-[#4A154B] leading-relaxed mb-4">
              We may disclose your information if required by law or in response to court orders, subpoenas, legal processes, government requests, protection of our rights, or investigation of fraud.
            </p>

            <h3 className="font-serif text-xl text-[#C9A689] mt-6 mb-3">4.4 Business Transfers</h3>
            <p className="text-[#4A154B] leading-relaxed">
              If we are involved in a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity. You will be notified of any such change.
            </p>
          </section>

          {/* Section 5 */}
          <section className="mb-12">
            <h2 className="font-serif text-2xl text-[#4A154B] mb-4 pb-2 border-b-2 border-[#C9A689]">5. DATA RETENTION</h2>
            <p className="text-[#4A154B] leading-relaxed mb-2">We retain your personal information for as long as your account is active or as needed to provide you with our Service:</p>
            <ul className="list-disc ml-6 text-[#4A154B] space-y-1">
              <li><strong>Active Accounts:</strong> Information is retained indefinitely while your account remains active</li>
              <li><strong>Inactive Accounts:</strong> If you do not use your account for 3 years, we may delete or anonymize your data</li>
              <li><strong>Transaction History:</strong> Retained for 7 years for accounting and legal compliance</li>
              <li><strong>Marketing Communications:</strong> Retained until you opt out</li>
            </ul>
          </section>

          {/* Section 6 */}
          <section className="mb-12">
            <h2 className="font-serif text-2xl text-[#4A154B] mb-4 pb-2 border-b-2 border-[#C9A689]">6. DATA SECURITY</h2>
            <p className="text-[#4A154B] leading-relaxed mb-2">We implement appropriate technical and organizational measures to protect your personal information:</p>
            <ul className="list-disc ml-6 text-[#4A154B] space-y-1 mb-4">
              <li><strong>Encryption:</strong> All data transmitted using SSL/TLS</li>
              <li><strong>Secure Authentication:</strong> Magic link authentication and JWT tokens with bcrypt hashing</li>
              <li><strong>QR Code Security:</strong> HMAC-signed codes with 5-minute expiration</li>
              <li><strong>Access Controls:</strong> Role-based access for staff and administrators</li>
              <li><strong>Regular Security Audits:</strong> Ongoing monitoring and testing</li>
            </ul>
            <div className="bg-gradient-to-r from-[#C9A689]/10 to-[#C9A689]/5 border-l-4 border-[#C9A689] rounded-r-lg p-5">
              <p className="text-[#4A154B]">
                <strong>However, no method of transmission over the internet is 100% secure.</strong> While we strive to protect your information, we cannot guarantee absolute security.
              </p>
            </div>
          </section>

          {/* Section 7 */}
          <section className="mb-12">
            <h2 className="font-serif text-2xl text-[#4A154B] mb-4 pb-2 border-b-2 border-[#C9A689]">7. YOUR PRIVACY RIGHTS</h2>
            <p className="text-[#4A154B] leading-relaxed mb-2">Depending on your location, you may have the following rights regarding your personal information:</p>
            <ul className="list-disc ml-6 text-[#4A154B] space-y-1">
              <li><strong>Right to Access:</strong> Request a copy of the personal information we hold about you</li>
              <li><strong>Right to Correction:</strong> Update or correct inaccurate information</li>
              <li><strong>Right to Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Right to Opt-Out:</strong> Opt out of marketing emails and certain data collection</li>
              <li><strong>Right to Data Portability:</strong> Request a copy of your data in machine-readable format</li>
              <li><strong>Right to Withdraw Consent:</strong> Withdraw consent for data processing at any time</li>
            </ul>
          </section>

          {/* Section 8 */}
          <section className="mb-12">
            <h2 className="font-serif text-2xl text-[#4A154B] mb-4 pb-2 border-b-2 border-[#C9A689]">8. CHILDREN'S PRIVACY</h2>
            <p className="text-[#4A154B] leading-relaxed">
              Our Service is intended for individuals aged 18 and older. We do not knowingly collect personal information from children under 18. If we discover that we have inadvertently collected information from a child under 18, we will delete it immediately.
            </p>
          </section>

          {/* Section 9-10 */}
          <section className="mb-12">
            <h2 className="font-serif text-2xl text-[#4A154B] mb-4 pb-2 border-b-2 border-[#C9A689]">9. INTERNATIONAL DATA TRANSFERS</h2>
            <p className="text-[#4A154B] leading-relaxed mb-8">
              Our Service is operated in the United States and Canada. If you access our Service from outside these regions, your information may be transferred to and processed in the United States or Canada.
            </p>

            <h2 className="font-serif text-2xl text-[#4A154B] mb-4 pb-2 border-b-2 border-[#C9A689]">10. COOKIES AND TRACKING TECHNOLOGIES</h2>
            <p className="text-[#4A154B] leading-relaxed mb-2">We use cookies and similar tracking technologies to enhance your experience:</p>
            <ul className="list-disc ml-6 text-[#4A154B] space-y-1">
              <li><strong>Essential Cookies:</strong> Required for authentication and basic functionality</li>
              <li><strong>Performance Cookies:</strong> Help us understand how you use our Service</li>
              <li><strong>Functional Cookies:</strong> Remember your preferences</li>
            </ul>
          </section>

          {/* Section 11 */}
          <section className="mb-12">
            <h2 className="font-serif text-2xl text-[#4A154B] mb-4 pb-2 border-b-2 border-[#C9A689]">11. CHANGES TO THIS PRIVACY POLICY</h2>
            <p className="text-[#4A154B] leading-relaxed">
              We may update this Privacy Policy from time to time. When we make material changes, we will notify you via email or prominent notice on our website. Your continued use of the Service after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          {/* Contact Box */}
          <section className="bg-gradient-to-br from-[#4A154B] to-[#5A255B] text-[#FFFFF0] rounded-2xl p-8 text-center mb-12">
            <h2 className="font-serif text-2xl text-[#C9A689] mb-4 pb-4 border-b-2 border-[#C9A689]">12. CONTACT US</h2>
            <p className="mb-4">If you have questions, concerns, or requests regarding this Privacy Policy:</p>
            <p className="mb-2"><strong>Email:</strong> <a href="mailto:info@oculusai.io" className="text-[#C9A689] hover:underline">info@oculusai.io</a></p>
            <p className="mb-2"><strong>Phone:</strong> (555) 123-4567</p>
            <p>
              <strong>Mailing Address:</strong><br />
              SalonO Rewards<br />
              123 Beauty Lane<br />
              Los Angeles, CA 90001
            </p>
          </section>

          {/* Section 13 */}
          <section className="mb-12">
            <h2 className="font-serif text-2xl text-[#4A154B] mb-4 pb-2 border-b-2 border-[#C9A689]">13. STATE-SPECIFIC PRIVACY RIGHTS</h2>
            
            <h3 className="font-serif text-xl text-[#C9A689] mt-6 mb-3">13.1 California Residents (CCPA/CPRA)</h3>
            <p className="text-[#4A154B] leading-relaxed mb-2">If you are a California resident, you have additional rights under the California Consumer Privacy Act:</p>
            <ul className="list-disc ml-6 text-[#4A154B] space-y-1 mb-2">
              <li>Right to know what personal information we collect and how we use it</li>
              <li>Right to delete your personal information</li>
              <li>Right to opt-out of the sale of personal information (we do not sell)</li>
              <li>Right to non-discrimination for exercising your rights</li>
            </ul>
            <p className="text-[#4A154B] leading-relaxed mb-4">
              <strong>To submit a CCPA request:</strong> Email info@oculusai.io with "CCPA Request" in the subject line.
            </p>

            <h3 className="font-serif text-xl text-[#C9A689] mt-6 mb-3">13.2 Canadian Residents (PIPEDA)</h3>
            <p className="text-[#4A154B] leading-relaxed mb-2">
              If you are a Canadian resident, your personal information is protected under PIPEDA. You have the right to access, challenge accuracy, request correction or deletion, and withdraw consent.
            </p>
            <p className="text-[#4A154B] leading-relaxed">
              <strong>To submit a PIPEDA request:</strong> Email info@oculusai.io with "PIPEDA Request" in the subject line.
            </p>
          </section>

          {/* Sections 14-16 */}
          <section className="mb-12">
            <h2 className="font-serif text-2xl text-[#4A154B] mb-4 pb-2 border-b-2 border-[#C9A689]">14. DATA BREACH NOTIFICATION</h2>
            <p className="text-[#4A154B] leading-relaxed mb-8">
              In the event of a data breach affecting your personal information, we will notify affected users within 72 hours and provide details about the breach, steps we're taking to address it, and advice on protecting yourself.
            </p>

            <h2 className="font-serif text-2xl text-[#4A154B] mb-4 pb-2 border-b-2 border-[#C9A689]">15. THIRD-PARTY LINKS</h2>
            <p className="text-[#4A154B] leading-relaxed mb-8">
              Our Service may contain links to third-party websites. We are not responsible for the privacy practices of these third parties. Please review their privacy policies before providing any personal information.
            </p>

            <h2 className="font-serif text-2xl text-[#4A154B] mb-4 pb-2 border-b-2 border-[#C9A689]">16. YOUR CONSENT</h2>
            <p className="text-[#4A154B] leading-relaxed">
              By using our Service, you consent to our Privacy Policy and agree to its terms.
            </p>
          </section>

          <hr className="border-[#C9A689]/30 my-10" />

          <p className="text-center italic text-[#C9A689]">
            <strong>Thank you for trusting SalonO Rewards with your personal information. We are committed to protecting your privacy and providing you with a secure, rewarding experience.</strong>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
